import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

const SPACING = 24;
const DOT = 1.5;
const POINTER_RADIUS = 170;
const FOCUS_HALO = 110;
const EASE = 0.16;

// A glow is a rounded box (half-size w × h, zero for the pointer) whose light fades out over r px from its edge.
type Glow = { x: number; y: number; w: number; h: number; r: number; s: number };

// `--primary: 262.1 83.3% 57.8%` → `hsla(262.1, 83.3%, 57.8%, a)`
const tone = (token: string, alpha: number) => {
  const [h = '0', s = '0%', l = '0%'] = token.trim().split(/\s+/);
  return `hsla(${h}, ${s}, ${l}, ${alpha.toFixed(3)})`;
};

/** The home hero's dot grid: the logo violet gathers under the pointer and around the prompt while it has focus. */
export function HeroDotField({ className }: { className?: string }) {
  const layerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const layer = layerRef.current;
    const canvas = canvasRef.current;
    const host = layer?.parentElement;
    const ctx = canvas?.getContext('2d');
    if (!layer || !canvas || !host || !ctx) return;

    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const base = document.createElement('canvas');
    const baseCtx = base.getContext('2d');
    let width = 0;
    let height = 0;
    let dpr = 1;
    let primary = '';
    let grid = '';
    let frame = 0;
    let pointer: { x: number; y: number } | null = null;
    let focusBox: HTMLElement | null = null;
    const glow: Glow = { x: 0, y: 0, w: 0, h: 0, r: POINTER_RADIUS, s: 0 };
    const target: Glow = { ...glow };

    const readTheme = () => {
      const css = getComputedStyle(document.documentElement);
      primary = css.getPropertyValue('--primary') || '262.1 83.3% 57.8%';
      const ink = css.getPropertyValue('--foreground') || '262 25% 9%';
      const dark = document.documentElement.classList.contains('dark');
      const key = `${width}x${height}@${dpr} ${primary} ${ink} ${dark}`;
      if (!baseCtx || !width || !height || key === grid) return false;
      grid = key;
      base.width = Math.round(width * dpr);
      base.height = Math.round(height * dpr);
      baseCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      baseCtx.clearRect(0, 0, width, height);
      baseCtx.fillStyle = tone(ink, dark ? 0.2 : 0.16);
      baseCtx.beginPath();
      for (let y = SPACING / 2; y < height; y += SPACING) {
        for (let x = SPACING / 2; x < width; x += SPACING) {
          baseCtx.moveTo(x + DOT, y);
          baseCtx.arc(x, y, DOT, 0, Math.PI * 2);
        }
      }
      baseCtx.fill();
      return true;
    };

    const draw = () => {
      if (!width || !height) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(base, 0, 0, width, height);
      if (glow.s < 0.01) return;
      const { x: cx, y: cy, w, h, r, s } = glow;
      const x0 = Math.max(SPACING / 2, Math.floor((cx - w - r) / SPACING) * SPACING + SPACING / 2);
      const y0 = Math.max(SPACING / 2, Math.floor((cy - h - r) / SPACING) * SPACING + SPACING / 2);
      for (let y = y0; y <= Math.min(height, cy + h + r); y += SPACING) {
        for (let x = x0; x <= Math.min(width, cx + w + r); x += SPACING) {
          const d = Math.hypot(Math.max(Math.abs(x - cx) - w, 0), Math.max(Math.abs(y - cy) - h, 0));
          if (d >= r) continue;
          const t = (1 - d / r) ** 2 * s;
          ctx.fillStyle = tone(primary, 0.2 + 0.65 * t);
          ctx.beginPath();
          ctx.arc(x, y, DOT + 1.6 * t, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const settled = () => (['x', 'y', 'w', 'h', 'r'] as const).every((k) => Math.abs(glow[k] - target[k]) < 0.5) && Math.abs(glow.s - target.s) < 0.005;

    const tick = () => {
      frame = 0;
      for (const k of ['x', 'y', 'w', 'h', 'r', 's'] as const) glow[k] += (target[k] - glow[k]) * EASE;
      if (settled()) Object.assign(glow, target);
      draw();
      if (!settled()) frame = window.requestAnimationFrame(tick);
    };

    const wake = () => {
      if (still || frame || document.visibilityState === 'hidden') return;
      frame = window.requestAnimationFrame(tick);
    };

    const aim = () => {
      const rect = canvas.getBoundingClientRect();
      if (pointer) Object.assign(target, { x: pointer.x - rect.left, y: pointer.y - rect.top, w: 0, h: 0, r: POINTER_RADIUS, s: 1 });
      else if (focusBox) {
        const box = focusBox.getBoundingClientRect();
        Object.assign(target, { x: box.left + box.width / 2 - rect.left, y: box.top + box.height / 2 - rect.top, w: box.width / 2, h: box.height / 2, r: FOCUS_HALO, s: 0.9 });
      } else target.s = 0;
      if (glow.s < 0.01) Object.assign(glow, { x: target.x, y: target.y, w: target.w, h: target.h, r: target.r });
      wake();
    };

    const resize = () => {
      const next = { dpr: Math.min(window.devicePixelRatio || 1, 2), width: host.clientWidth, height: host.clientHeight };
      if (next.dpr === dpr && next.width === width && next.height === height) return;
      ({ dpr, width, height } = next);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      readTheme();
      draw();
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      pointer = { x: e.clientX, y: e.clientY };
      aim();
    };
    const onLeave = () => { pointer = null; aim(); };
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el || !/^(TEXTAREA|INPUT)$/.test(el.tagName)) return;
      focusBox = (el.closest('form') as HTMLElement | null) ?? el;
      aim();
    };
    const onFocusOut = () => { focusBox = null; aim(); };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (frame) window.cancelAnimationFrame(frame);
        frame = 0;
      } else draw();
    };
    const onScroll = () => { if (focusBox) aim(); };

    const sizeWatch = new ResizeObserver(resize);
    sizeWatch.observe(host);
    const themeWatch = new MutationObserver(() => { if (readTheme()) draw(); });
    themeWatch.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style', 'data-theme'] });
    if (!still) {
      host.addEventListener('pointermove', onMove as EventListener);
      host.addEventListener('pointerleave', onLeave);
      host.addEventListener('focusin', onFocusIn);
      host.addEventListener('focusout', onFocusOut);
      host.addEventListener('scroll', onScroll, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      sizeWatch.disconnect();
      themeWatch.disconnect();
      host.removeEventListener('pointermove', onMove as EventListener);
      host.removeEventListener('pointerleave', onLeave);
      host.removeEventListener('focusin', onFocusIn);
      host.removeEventListener('focusout', onFocusOut);
      host.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div ref={layerRef} aria-hidden="true" className={cn('pointer-events-none absolute inset-0 -z-10 overflow-hidden', className)}>
      <canvas ref={canvasRef} className="absolute left-0 top-0" />
    </div>
  );
}
